import { RatePickerProps } from "@/components/Inputs/RatesPicker/RatePickerProps.ts";
import { RatePlanDetails } from "@/components/Inputs/RatesPicker/components/RatePlanDetails.tsx";
import { useCheckAccommodationAvailibility } from "@/components/Inputs/RatesPicker/hooks/useCheckAccommodationAvailibility.ts";
import { clearReservationOfferURLParam } from "@/helpers/clearReservationOfferURLParam.ts";
import { handleGTMAddAccommodationToCart } from "@/helpers/gtm/handleGTMAddAccommodationToCart.ts";
import { useScreenSize } from "@/helpers/hooks/useScreenSize.tsx";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { isMaterraNewYearsPackage } from "@/helpers/isMaterraNewYearsPackage.ts";
import { mergeRatePlans } from "@/helpers/mergeRatePlans.ts";
import { scrollToTop } from "@/helpers/scrollToTop.ts";
import { useGetRPTranslated } from "@/helpers/translation/useGetRPTranslated.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { ModalKey } from "@/stores/ModalStore/ModalKey.ts";
import { useModalStore } from "@/stores/ModalStore/useModalStore.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const RatesPicker = (props: RatePickerProps) => {
  const { addCartItem, cartAccommodationItems, setPage, defaultDateRange } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
      defaultDateRange: state.defaultDateRange,
      addCartItem: state.addCartItem,
      setPage: state.setPage,
    })),
  );
  const openModal = useModalStore((s) => s.openModal);
  const { getTranslateRPData } = useGetRPTranslated();
  const { t } = useTranslation();
  const screenSize = useScreenSize();
  const isRGUnavailable = useCheckAccommodationAvailibility(props.rateGroup);

  const areAllRPsUnavailable = props.rateGroup.ratePlans.every((rp) => !rp.isAvailable);
  const ratePlans = useMemo(() => mergeRatePlans(props.rateGroup), [props.rateGroup]);

  const handleAddRPToStore = (rp: RatePlanMerged) => addCartItem({ ...rp, rateGroup: props.rateGroup });

  const datesNotSelected = useMemo(() => {
    return !defaultDateRange?.[0] || !defaultDateRange?.[1];
  }, [defaultDateRange]);

  const RPRendered = ratePlans?.map((RP) => {
    if (!RP.isAvailable) return null;

    const handleSelectRatePlan = () => {
      handleAddRPToStore(RP);
      props.onSelectRatePlan?.(RP);
      handleGTMAddAccommodationToCart(RP, props.rateGroup);
      clearReservationOfferURLParam();
      setPage(AppPage.ExtraOptions);
      scrollToTop();
    };

    const renderPriceAndSelect = () => {
      return (
        <>
          <RatePlanDetails hasMoreThanOneRP={ratePlans.length > 1} ratePlan={RP} />
          <div className="c-rate__right">
            <button
              className="c-be-button"
              onClick={handleSelectRatePlan}
              disabled={isRGUnavailable || !RP.isAvailable || datesNotSelected}
            >
              <span className="c-be-button__label">
                {isRGUnavailable || !RP.isAvailable ? t("noUnitsLeft") : t("select")}
              </span>
            </button>
          </div>
        </>
      );
    };

    const isRatePlanUsed = cartAccommodationItems.some((item) => item.ratePlan.id === RP.id);
    const { name, description } = getTranslateRPData(RP);

    const renderName = () => {
      return (
        <h5 className="u-h6 u-fw-600 c-rate__title">
          {name}{" "}
          {RP?.cancelationPolicy?.id && (
            <span
              className="c-rate__cancellationPolicy"
              onClick={(e) => {
                e.stopPropagation();
                openModal({ editObj: RP?.cancelationPolicy, key: ModalKey.RPCancelationPolicy });
              }}
            >
              ({t("generalTermsAndConditions")})
            </span>
          )}
        </h5>
      );
    };

    const renderDescription = () => {
      const hasDescription = Boolean(description);
      if (!hasDescription) return null;

      return <p className="u-p1 c-rate__description">{description}</p>;
    };

    return (
      <li
        key={RP.id}
        className={`c-rate ${isRatePlanUsed ? "is-selected" : ""} ${
          isMaterraNewYearsPackage(RP.id) ? "materra-new-years-package" : ""
        }`}
      >
        <div className="c-rate__left">
          {renderName()}
          {renderDescription()}
        </div>
        {screenSize.width < 800 ? (
          <div className="c-rate__mobile_price">{renderPriceAndSelect()}</div>
        ) : (
          renderPriceAndSelect()
        )}
      </li>
    );
  });

  if (areAllRPsUnavailable) return null;

  return <ul className="c-rates">{RPRendered}</ul>;
};
